import { enableProdMode, importProvidersFrom } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';

import { provideServiceWorker } from '@angular/service-worker';
import { provideTranslateService } from '@ngx-translate/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  AdblockDetectorService,
  BodyService,
  CONSTANTS_LOADER,
  CustomSerializer,
  DateTimeService,
  DEFAULT_ERROR_MESSAGES,
  ImmomioTheme,
  InfrastructureConfig,
  InfrastructureModule,
  NewRelicService,
  provideKeycloak,
  provideResidentApolloConfig,
  ThemeModule,
  ThemeUrls,
  UserConversionService
} from '@ui/legacy-lib';
import { ReactiveFormsModule } from '@angular/forms';
import { provideStore } from '@ngrx/store';

import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideRouterStore } from '@ngrx/router-store';

import { provideNgxMask } from 'ngx-mask';
import { provideLottieOptions } from 'ngx-lottie';
import {
  provideRouter,
  withComponentInputBinding,
  withRouterConfig
} from '@angular/router';
import { provideAngularSvgIcon } from 'angular-svg-icon';
import { provideImmomioFormErrorMessages } from '@immomio/ui/form';
import { ENVIRONMENT_CONFIG } from '@ui/legacy-lib';
import { provideTranslatedTitleStrategy } from '@ui/shared/util';
import { storageKeys } from '@ui/tenant-pool/shared/config/util';
import { provideSignalFormsConfig } from '@angular/forms/signals';
import { NG_STATUS_CLASSES } from '@angular/forms/signals/compat';
import { languageConfig } from './app/config/language.config';
import { AppComponent } from './app/app.component';
import { ROUTES } from './app/app.router';
import { ConstantsLoaderService } from './app/core/loaders/constants-loader.service';
import { FormStorageService } from './app/auth/services/form-storage.service';
import { GuestFormService } from './app/auth/guest-registration/services/guest-form.service';
import { appReducers } from './app/+state/reducers';
import { AuthEffects } from './app/+state/auth/auth.effects';
import { BrandingEffects } from './app/+state/branding/branding.effects';
import { CustomQuestionsEffects } from './app/+state/custom-questions/custom-questions.effects';
import { DigitalContractEffects } from './app/+state/digital-contract/digital-contract.effects';
import { GuestModeEffects } from './app/+state/guest-mode/guest-mode.effects';
import { PaymentsEffects } from './app/+state/payments/payments.effects';
import { PropertyMatchesEffects } from './app/+state/property-matches/property-matches.effects';
import { RentDepositEffects } from './app/+state/rent-deposit/rent-deposit.effects';
import { ResidentEffects } from './app/+state/resident/resident.effects';
import { SelfDisclosureEffects } from './app/+state/self-disclosure/self-disclosure.effects';
import { environment } from './environments/environment';

if (environment.production) {
  enableProdMode();
}

const infrastructureConfig: InfrastructureConfig = {
  environment,
  apolloCacheIds: {
    MessageSource: 'messageKey'
  },
  storageKeys,
  languages: languageConfig
};

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      ReactiveFormsModule.withConfig({
        callSetDisabledState: 'whenDisabledForLegacyCode'
      }),
      InfrastructureModule.forRoot(infrastructureConfig),
      ThemeModule.forRoot({
        themes: [ImmomioTheme],
        active: ThemeUrls.IMMOMIO
      })
    ),
    provideResidentApolloConfig(),
    provideTranslateService(),
    provideRouter(
      ROUTES,
      withComponentInputBinding(),
      withRouterConfig({
        onSameUrlNavigation: 'reload',
        urlUpdateStrategy: 'eager',
        paramsInheritanceStrategy: 'always'
      })
    ),
    provideTranslatedTitleStrategy(),
    provideKeycloak(),
    { provide: CONSTANTS_LOADER, useClass: ConstantsLoaderService },
    { provide: ENVIRONMENT_CONFIG, useValue: environment },
    provideLottieOptions({
      player: () => import('lottie-web')
    }),
    DateTimeService,
    AdblockDetectorService,
    UserConversionService,
    GuestFormService,
    FormStorageService,
    BodyService,
    NewRelicService,
    provideStore(appReducers),
    provideEffects([
      AuthEffects,
      PropertyMatchesEffects,
      CustomQuestionsEffects,
      SelfDisclosureEffects,
      BrandingEffects,
      DigitalContractEffects,
      GuestModeEffects,
      RentDepositEffects,
      PaymentsEffects,
      ResidentEffects
    ]),
    !environment.production
      ? provideStoreDevtools({ connectInZone: true })
      : [],
    provideRouterStore({
      serializer: CustomSerializer
    }),
    provideNgxMask(),
    provideServiceWorker('/ngsw-worker.js', {
      enabled:
        environment.deploymentEnv === 'production' ||
        environment.deploymentEnv === 'sandbox'
    }),
    provideAnimations(),
    provideAngularSvgIcon(),
    provideImmomioFormErrorMessages(DEFAULT_ERROR_MESSAGES),
    provideSignalFormsConfig({ classes: NG_STATUS_CLASSES })
  ]
}).catch(err => console.error(err));
